import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Utility function to merge Tailwind CSS classes safely.
 * 
 * Combines clsx (conditional class joining) with tailwind-merge
 * (intelligent Tailwind class deduplication) for reliable class composition.
 * 
 * @param inputs - Any number of class values (strings, arrays, objects, etc.)
 * @returns Merged and deduplicated class string
 * 
 * @example
 * ```tsx
 * // Basic usage
 * cn("px-4", "py-2") // "px-4 py-2"
 * 
 * // With conditionals
 * cn("base", isActive && "active", !isDisabled && "enabled")
 * 
 * // Tailwind conflict resolution
 * cn("px-2", "px-4") // "px-4" (later value wins)
 * cn("text-red-500", "text-blue-500") // "text-blue-500"
 * ```
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format a date string for display.
 * 
 * @param date - Date string or Date object
 * @param options - Intl.DateTimeFormat options
 * @returns Formatted date string
 * 
 * @example
 * ```tsx
 * formatDate("2024-01-15") // "Jan 15, 2024"
 * formatDate(new Date(), { weekday: "long" }) // "Monday, Jan 15, 2024"
 * ```
 */
export function formatDate(
  date: string | Date,
  options: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    year: "numeric",
  }
): string {
  const dateObj = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat("en-US", options).format(dateObj);
}

/**
 * Truncate text to a maximum length with ellipsis.
 * 
 * @param text - Text to truncate
 * @param maxLength - Maximum length before truncation
 * @returns Truncated text with "..." if needed
 * 
 * @example
 * ```tsx
 * truncateText("Hello World", 5) // "Hello..."
 * truncateText("Hi", 5) // "Hi"
 * ```
 */
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength).trim()}...`;
}

/**
 * Capitalize the first letter of each word in a string.
 * 
 * @param text - Text to capitalize
 * @returns Title-cased text
 * 
 * @example
 * ```tsx
 * capitalizeWords("hello world") // "Hello World"
 * capitalizeWords("brain & focus") // "Brain & Focus"
 * ```
 */
export function capitalizeWords(text: string): string {
  return text
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * Generate a unique ID for client-side use.
 * Not cryptographically secure - use only for UI purposes.
 * 
 * @returns Random string ID
 */
export function generateId(): string {
  return Math.random().toString(36).substring(2, 11);
}

/**
 * Debounce a function to limit how often it can fire.
 * 
 * @param fn - Function to debounce
 * @param delay - Delay in milliseconds
 * @returns Debounced function
 * 
 * @example
 * ```tsx
 * const debouncedSearch = debounce((query) => search(query), 300);
 * input.addEventListener("input", (e) => debouncedSearch(e.target.value));
 * ```
 */
export function debounce<T extends (...args: any[]) => any>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}
